import React, { useEffect, useState } from "react";
import { Mail, Save, Send, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const inputCls = "mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none";
const inputStyle = { borderColor: "var(--border)", background: "#fff" };
const F = ({ label, children }) => (
  <div><label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</label>{children}</div>
);

const PROVIDERS = [
  { id: "brevo", title: "Servizio di invio (consigliato)", desc: "Mittente Aruba, invio via Brevo: non soggetto ai blocchi IP" },
  { id: "smtp", title: "SMTP Aruba diretto", desc: "Può essere rifiutato se l'IP del server è in blacklist" },
];

const ProviderPicker = ({ value, onChange }) => (
  <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="email-provider-picker">
    {PROVIDERS.map((p) => (
      <button key={p.id} type="button" onClick={() => onChange(p.id)} data-testid={`email-provider-${p.id}`}
        className="text-left p-3 rounded-md border transition-colors"
        style={{ borderColor: value === p.id ? "var(--primary)" : "var(--border)", background: value === p.id ? "#F4F7FB" : "#fff" }}>
        <div className="text-sm font-semibold">{p.title}</div>
        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{p.desc}</div>
      </button>
    ))}
  </div>
);

const BrevoFields = ({ f, set }) => (
  <>
    <div className="md:col-span-2">
      <F label={`Chiave API Brevo${f.has_brevo_key ? " (lascia vuoto per mantenerla)" : ""}`}>
        <input type="password" value={f.brevo_api_key} onChange={set("brevo_api_key")} autoComplete="new-password" placeholder="xkeysib-..." className={inputCls} style={inputStyle} data-testid="email-brevo-key" />
      </F>
    </div>
    <div className="md:col-span-2 text-xs rounded-md p-3" style={{ background: "#FFF8E1", color: "#5D4300" }} data-testid="email-brevo-help">
      Brevo → Impostazioni → Mittenti, domini e IP: aggiungi il dominio Aruba e copia i record DNS nel pannello Aruba, poi verifica il mittente. La chiave si crea in Impostazioni → SMTP e API → Chiavi API. Le risposte arrivano alla casella Aruba del mittente.
    </div>
  </>
);

export default function EmailArubaConfig() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/email-config").then(({ data }) => { if (alive) setF((cur) => cur || { ...data, smtp_password: "", brevo_api_key: "" }); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/email-config", { ...f, smtp_port: Number(f.smtp_port) || 465 });
      setF({ ...data, smtp_password: "", brevo_api_key: "" });
      toast.success("Casella email salvata");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/email-config/test", { to: testTo });
      toast.success(`Email di prova inviata a ${data.to}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setTesting(false); }
  };

  return (
    <div className="card-shell overflow-hidden" data-testid="email-aruba-config">
      <div className="p-5 border-b flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
        <Mail className="w-5 h-5" style={{ color: "var(--primary)" }} />
        <div className="flex-1">
          <h2 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Email Aruba · invii alle commesse</h2>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>Casella usata per inviare PDF sopralluogo/installazione e link alle commesse.</p>
        </div>
        <span className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: f.configured ? "#1B5E20" : "#B30000" }} data-testid="email-config-status">
          {f.configured ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {f.configured ? "Configurata" : "Da configurare"}
        </span>
      </div>
      <form onSubmit={save} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <ProviderPicker value={f.provider} onChange={(provider) => setF({ ...f, provider })} />
        {f.provider === "brevo" ? <BrevoFields f={f} set={set} /> : <>
        <F label="Server SMTP"><input value={f.smtp_host} onChange={set("smtp_host")} className={inputCls} style={inputStyle} data-testid="email-smtp-host" /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Porta"><input type="number" value={f.smtp_port} onChange={set("smtp_port")} className={inputCls} style={inputStyle} data-testid="email-smtp-port" /></F>
          <label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" checked={!!f.smtp_ssl} onChange={set("smtp_ssl")} className="w-4 h-4 accent-[var(--primary)]" data-testid="email-smtp-ssl" /> SSL</label>
        </div>
        <F label="Utente (indirizzo Aruba)"><input value={f.smtp_user} onChange={set("smtp_user")} placeholder="ufficiotecnico@weprofit.it" className={inputCls} style={inputStyle} data-testid="email-smtp-user" /></F>
        <F label={`Password${f.has_password ? " (lascia vuoto per mantenerla)" : ""}`}><input type="password" value={f.smtp_password} onChange={set("smtp_password")} autoComplete="new-password" className={inputCls} style={inputStyle} data-testid="email-smtp-password" /></F>
        </>}
        <F label="Mittente (email Aruba)"><input value={f.from_email} onChange={set("from_email")} placeholder={f.provider === "brevo" ? "ufficiotecnico@weprofit.it" : "uguale all'utente se vuoto"} className={inputCls} style={inputStyle} data-testid="email-from-email" /></F>
        <F label="Nome mittente"><input value={f.from_name} onChange={set("from_name")} className={inputCls} style={inputStyle} data-testid="email-from-name" /></F>
        <div className="md:col-span-2">
          <F label="Firma in fondo all'email"><textarea rows={2} value={f.signature} onChange={set("signature")} className={inputCls} style={inputStyle} data-testid="email-signature" /></F>
        </div>
        <div className="md:col-span-2 flex flex-wrap gap-2 items-center">
          <button type="submit" disabled={saving} className="btn-primary" data-testid="email-config-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva</button>
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Email per la prova (vuoto = la tua)" className="px-3 py-2 text-sm rounded-md border outline-none flex-1 min-w-[200px]" style={inputStyle} data-testid="email-test-to" />
          <button type="button" onClick={test} disabled={testing || !f.configured} className="btn-secondary disabled:opacity-50" data-testid="email-config-test">{testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Invia prova</button>
        </div>
      </form>
    </div>
  );
}
